import React, { Component } from 'react';
import moment from 'moment';
import PrintableValueWithLabel from './common/PrintableValueWithLabel';
import SimpleTable, { SimpleTableProps } from './common/SimpleTable';
import { parseCurrency } from '../utility/CurrencyFormatter';
import { grossReceiptTypes } from '../utility/Constants';
import { ReturnLocation, FoodBeverageReturnLocation, SplitVehicleLocation, RoomOccupancyLocation } from '../types/LocationTypes';

export interface PrintHistoryState {
    returnTypeDescription: string;
    returnTypeCode: grossReceiptTypes;
    accountNum: number;
    pin: string;
    totalSalesValue?: number;
    excessTaxAmount?: number;
    currentDue?: number;
    creditApplied: number;
    creditRefunded: number;
    creditRemaining: number;
    postmarkDate?: Date;
    dueDate?: Date;
    ownerName?: string;
    ownerAddressLine1?: string;
    ownerAddressLine2?: string;
    ownerAddressLine3?: string;
    locations?: ReturnLocation[];
    taxTotal?: number;
    penaltiesTotal?: number;
    billDisplay?: string;
    bill?: GRBillState,
    returnMonth: number,
    returnYear: number,
    returnFiscalYear: number
    filedBy: string,
    splitVehicleUnderByYear: { [key: number]: number | null },
    splitVehicleOverByYear: { [key: number]: number | null }
}

export interface GRBillState {
    billNumber: string;
    taxYear: string;
}

export interface FoodBeveragePrintHistoryState extends PrintHistoryState {
    returnTypeCode: grossReceiptTypes.foodBev;
    locations?: FoodBeverageReturnLocation[];
}

export interface SplitValueVehiclePrintHistoryState extends PrintHistoryState {
    returnTypeCode: grossReceiptTypes.vehicle;
    locations?: SplitVehicleLocation[];
}

export interface RoomOccupancyPrintHistoryState extends PrintHistoryState {
    locations?: RoomOccupancyLocation[];
    returnTypeCode: grossReceiptTypes.roomOccupancy;
}

function isFoodBeverage(grReturn: (PrintHistoryState | FoodBeveragePrintHistoryState | RoomOccupancyPrintHistoryState | SplitValueVehiclePrintHistoryState)): grReturn is FoodBeveragePrintHistoryState {
    return grReturn.returnTypeCode === grossReceiptTypes.foodBev;
}

function isRoomOccupancy(grReturn: (PrintHistoryState | FoodBeveragePrintHistoryState | RoomOccupancyPrintHistoryState | SplitValueVehiclePrintHistoryState)): grReturn is RoomOccupancyPrintHistoryState {
    return grReturn.returnTypeCode === grossReceiptTypes.roomOccupancy;
}

function isSplitValueVehicle(grReturn: (PrintHistoryState | FoodBeveragePrintHistoryState | RoomOccupancyPrintHistoryState | SplitValueVehiclePrintHistoryState)): grReturn is SplitValueVehiclePrintHistoryState {
    return grReturn.returnTypeCode === grossReceiptTypes.vehicle && grReturn.splitVehicleUnderByYear[grReturn.returnFiscalYear] !== null;
}

const defaultLocationHeaders = [
    "Name",
    "Address",
    "Receipts",
];

export default class PrintableReturnInfo extends Component<PrintHistoryState | FoodBeveragePrintHistoryState | RoomOccupancyPrintHistoryState> {
    static displayName = PrintableReturnInfo.name;

    render() {
        return (
            <div className="printable-return-info">
                {this.accountHeader}
                <div className="centered-container">
                    <div className="container-inputs printable-inputs">
                        {this.propsAsArray.map((prop, index) => {

                            if (prop.label === "Balance Due" || prop.label === "Credit To Be Applied") {
                                return(
                                    <React.Fragment key={index}>
                                        <hr className="horizontal-line" />
                                        <PrintableValueWithLabel label={prop.label} value={prop.value} note={prop.note} />
                                    </React.Fragment>
                                )
                                
                            } else {
                                return <PrintableValueWithLabel key={index} label={prop.label} value={prop.value} note={prop.note} />
                            }
                        })}
                        <div className="additional-container centered-container">
                            {this.formattedAddress}
                        </div>
                        {this.locationsWithHeader}
                    </div>
                </div>
            </div>
        );
    }

    get accountHeader(): JSX.Element {
        const periodEndingLabel = this.props.returnTypeCode === grossReceiptTypes.heavyEquipment
            ? 'Quarter Ending'
            : 'Month Ending';

        const headerItems = [
            [
                {
                    label: "Account",
                    value: this.props.accountNum,
                },
                {
                    label: "Bill Number",
                    value: this.props.billDisplay,
                },
            ],
            [
                {
                    label: "Return Type",
                    value: this.props.returnTypeDescription,
                },
                {
                    label: "Date Submitted",
                    value: moment(this.props.postmarkDate).format('M/D/YYYY'),
                },
            ],
            [
                {
                    label: periodEndingLabel,
                    value: moment(`${this.props.returnYear}-${this.props.returnMonth}-1`).endOf('month').format("M/D/YYYY")
                }
            ],
            [
                {
                    label: "Bill Due Date",
                    value: moment.utc(this.props.dueDate).format('M/D/YYYY'),
                },
            ],
            [
                {
                    label: "Filed By",
                    value: this.props.filedBy,
                },
            ]
        ];
        return (
            <div className="printable-account-header">
                {headerItems.map((row, rowIndex) =>
                    <div key={rowIndex} className="printable-header-row">
                        {row.map((header, index) => (
                            <span key={index} className="printable-header-item">
                                <span className="bold">
                                    {`${header.label}: `}
                                </span>
                                {header.value}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    get locationsWithHeader(): JSX.Element {
        const grReturn = this.props;
        if (grReturn.locations === undefined || grReturn.locations.length === 0) {
            return (<></>);
        }

        let tableData;
        if (isFoodBeverage(grReturn)) {
            tableData = this.parseFoodBeverageRows(grReturn)
        } else if (isSplitValueVehicle(grReturn)) {
            tableData = this.parseSplitValueVehicleRows(grReturn);
        } else if (isRoomOccupancy(grReturn)) {
            tableData = this.parseRoomOccupancyRows(grReturn);
        } else {
            tableData = this.parseNonFoodBeverage(grReturn);
        }

        return (
            <div className="location-table-container centered">
                <div id="locationTitle">
                    Locations
                </div>
                <SimpleTable {...tableData} />
            </div>
        );
    }

    parseRowFromLocation = (location: ReturnLocation) => {
        return [
            location.name,
            location.address,
            parseCurrency(Number(location.grossReceipts ?? 0)),
        ];
    }

    parseFoodBeverageRows = (grReturn: FoodBeveragePrintHistoryState): SimpleTableProps => {
        return {
            headers: defaultLocationHeaders.concat(["Non-Prepared Receipts"]),
            rows: (grReturn.locations || []).map(location => {
                let row = this.parseRowFromLocation(location);
                return row.concat([parseCurrency(Number(location.nonPreparedFoodBeverageReceipts ?? 0))])
            }),
        };
    }

    parseRoomOccupancyRows = (grReturn: RoomOccupancyPrintHistoryState): SimpleTableProps => {
        return {
            headers: ["Name", "Address", "Receipts", "Extended Stays", "Government Exemptions", "Non Occupancy"],
            rows: (grReturn.locations || []).map(location => {
                return [
                    location.name,
                    location.address,
                    parseCurrency(Number(location.grossReceipts ?? 0)),
                    parseCurrency(Number(location.extendedStays ?? 0)),
                    parseCurrency(Number(location.governmentExemptions ?? 0)),
                    parseCurrency(Number(location.nonOccupancy ?? 0)),                 
                ]
            })
        };
    }

    parseSplitValueVehicleRows = (grReturn: SplitValueVehiclePrintHistoryState): SimpleTableProps => {
        return {
            headers: [
                "Name",
                "Address",
                `Under ${grReturn.splitVehicleUnderByYear[grReturn.returnFiscalYear]?.toLocaleString()} lbs`,
                grReturn.splitVehicleOverByYear[grReturn.returnFiscalYear]
                    ? `${grReturn.splitVehicleUnderByYear[grReturn.returnFiscalYear]?.toLocaleString()} - ${grReturn.splitVehicleOverByYear[grReturn.returnFiscalYear]?.toLocaleString() } lbs`
                    : `Over ${grReturn.splitVehicleUnderByYear[grReturn.returnFiscalYear]?.toLocaleString() } lbs`,
                "Total Receipts",
            ],
            rows: (grReturn.locations || []).map(location => {
                return [
                    location.name,
                    location.address,
                    parseCurrency(Number(location.grossReceiptsUnder ?? 0)),
                    parseCurrency(Number(location.grossReceiptsOver ?? 0)),
                    parseCurrency(Number(location.grossReceipts ?? 0)),
                ]
            })
        };
    }

    parseNonFoodBeverage = (grReturn: PrintHistoryState | RoomOccupancyPrintHistoryState): SimpleTableProps => {
        return {
            headers: defaultLocationHeaders,
            rows: (grReturn.locations || []).map(this.parseRowFromLocation),
        }
    }

    get propsAsArray(): PrintableValueWithLabelProps[] {
        const grReturn = this.props;
        let values = [
            {
                label: "Total Sales Value",
                value: parseCurrency(grReturn.totalSalesValue),
            },
            {
                label: "Excess Tax Amount",
                value: parseCurrency(grReturn.excessTaxAmount),
            },
            {
                label: "Current Due",
                value: parseCurrency(grReturn.currentDue),
            },
            {
                label: "Current Due does not include the credit to be applied",
                value: "",
                note: true
            },
            {
                label: "Credit Refunded",
                value: parseCurrency(grReturn.creditRefunded),
            },
            {
                label: "Credit To Be Applied",
                value: parseCurrency(grReturn.creditApplied),
            },
            {
                label: "Penalties Total",
                value: parseCurrency(grReturn.penaltiesTotal),
            },
            {
                label: "Tax Total",
                value: parseCurrency(grReturn.taxTotal),
            },
            {
                label: "Balance Due",
                value: this.calculateBalenceDue(grReturn.currentDue, grReturn.creditApplied)
            },
        ];

        return values;
    }

    calculateBalenceDue(currentDue: number | undefined, creditApplied: number): string {
        if (currentDue === undefined) {
            return 'Error';
        }
    return parseCurrency(currentDue - creditApplied);
    };

    get formattedAddress(): JSX.Element {
        let addressParts = [
            this.props.ownerName,
            this.props.ownerAddressLine1,
            this.props.ownerAddressLine3,
        ];
        // Only include address 2 if it is supplied
        if (this.props.ownerAddressLine2) {
            addressParts.splice(2, 0, this.props.ownerAddressLine2);
        }
        return (
            <div className="printable-owner-address">
                {addressParts.map((part, index) => (
                    <div key={index}>
                        {part}
                    </div>
                 ))}
            </div>
        );
    }
}
